import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { reportService } from "../../Services";
import { utils } from "../../Helpers/utils";
import {
  EditorInput,
  playIconReport,
  pauseIconReport,
} from "../../Components";
import {
  EditorState,
  convertToRaw,
  convertFromRaw,
  ContentState,
} from "draft-js";
import AddInfluencer from "./AddInfluencer";
import { ArrowLeft, Loader2 } from "lucide-react";

const initForm = {
  title: "",
  description: "",
  start_date: "",
  end_date: "",
  status: "",
  platform_id: "",
  user_id: "",
  influencer_report: [],
};

const CustomTextInput = ({
  label,
  value,
  onChange,
  error,
  errorText,
  readOnly,
}) => (
  <div className="">
    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
      {label}
      <span className="text-red-500">*</span>
    </label>
    <input
      type="text"
      value={value || ""}
      onChange={onChange}
      readOnly={readOnly}
      placeholder={`Enter ${label.toLowerCase()}...`}
      className={`w-full p-3 border rounded-lg transition-colors text-gray-900 placeholder-gray-400 ${readOnly ? "bg-gray-50 cursor-not-allowed" : ""
        }`}
    />
    {error && errorText && !value && (
      <div className="text-sm text-red-600 mt-2">
        <div className="flex items-center gap-2">{errorText}</div>
      </div>
    )}
  </div>
);

const CustomSelect = ({
  label,
  selected,
  onChange,
  list,
  error,
  errorText,
  readOnly,
}) => (
  <div className="">
    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
      {label}
      <span className="text-red-500">*</span>
    </label>
    <div className="relative custom-dropdown">
      <select
        value={selected?.id || ""}
        onChange={(e) => {
          const selectedItem = list.find(
            (item) => String(item.id) === String(e.target.value)
          );
          if (selectedItem) onChange(selectedItem);
        }}
        disabled={readOnly}
        className={`w-full p-3 pr-10 border rounded-lg transition-colors bg-white appearance-none cursor-pointer ${readOnly ? "bg-gray-50 cursor-not-allowed" : ""
          } ${!selected ? "text-gray-400" : "text-gray-900"}`}
      >
        <option value="" disabled>
          Select {label.toLowerCase()}...
        </option>
        {list?.map((option) => (
          <option key={option.id} value={option.id} className="text-gray-900">
            {option.name}
          </option>
        ))}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none z-10">
        <svg
          className="w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
    </div>
    {error && errorText && !selected && (
      <div className="text-sm text-red-600 mt-2">
        <div className="flex items-center gap-2">{errorText}</div>
      </div>
    )}
  </div>
);

const CustomDateInput = ({
  label,
  value,
  onChange,
  error,
  errorText,
  readOnly,
  minDate,
}) => {
  const formatDateValue = (dateValue) => {
    if (!dateValue) return "";
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return "";
      return format(date, "yyyy-MM-dd");
    } catch (e) {
      return "";
    }
  };

  const formatMinDate = (minDateValue) => {
    if (!minDateValue) return undefined;
    try {
      const date = new Date(minDateValue);
      if (isNaN(date.getTime())) return undefined;
      return format(date, "yyyy-MM-dd");
    } catch (e) {
      return undefined;
    }
  };

  return (
    <div className="">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
        {label}
        <span className="text-red-500">*</span>
      </label>
      <input
        type="date"
        value={formatDateValue(value)}
        onChange={(e) => {
          const newDate = new Date(e.target.value);
          if (!isNaN(newDate.getTime())) {
            onChange(newDate);
          }
        }}
        min={formatMinDate(minDate)}
        readOnly={readOnly}
        className={`w-full p-3 border rounded-lg transition-colors cursor-pointer ${readOnly ? "bg-gray-50 cursor-not-allowed" : ""
          } ${!value ? "text-gray-400" : "text-gray-900"}`}
        onClick={(e) => {
          if (!readOnly && e.target.showPicker) {
            e.target.showPicker();
          }
        }}
      />
      {error && errorText && !value && (
        <div className="text-sm text-red-600 mt-2">
          <div className="flex items-center gap-2">{errorText}</div>
        </div>
      )}
    </div>
  );
};

export default function CreateReport({
  tokenAvailable,
  platform,
  restoreReport,
  archiveReport,
  statusActivePaused,
  setConfirmFor,
  currentStatus,
  updateStatus,
  addToast,
  setTab,
  setToastList,
  toastList,
  selectedReport: propsSelectedReport,
  onHeaderTitleChange,
  refreshReports, // Added this prop
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(id ? true : false);
  const [isSaving, setIsSaving] = useState(false);
  const mode = id ? "edit" : "create";

  // Initialize data state
  const [data, setData] = useState([
    {
      key: Date.now(),
      infHandle: "",
      hash_tag: "",
      token: "-",
    },
  ]);

  const [error, setError] = useState(false);
  const [infError, setInfError] = useState(false);
  const [hasDuplicateHandles, setHasDuplicateHandles] = useState(false);
  const [form, setForm] = useState(initForm);
  const [readonly, setReadOnly] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  // Fetch report data if in edit mode
  useEffect(() => {
    const fetchReportData = async () => {
      if (mode === "edit" && id) {
        try {
          setIsLoading(true);
          const res = await reportService.getReport(id);

          // Handle different response structures
          let fetchedReport;
          if (Array.isArray(res.data)) {
            fetchedReport = res.data[0];
          } else if (res.data && typeof res.data === 'object') {
            fetchedReport = res.data;
          } else {
            fetchedReport = res.data;
          }

          if (!fetchedReport) {
            throw new Error("Report not found");
          }

          // Process dates safely
          const processDate = (dateString) => {
            if (!dateString || dateString === "" || dateString === "0000-00-00") {
              return "";
            }
            try {
              // Handle different date formats
              const date = new Date(dateString);
              if (isNaN(date.getTime())) {
                // Try alternative parsing
                const altDate = new Date(dateString.replace(/-/g, '/'));
                return isNaN(altDate.getTime()) ? "" : altDate;
              }
              return date;
            } catch (e) {
              return "";
            }
          };

          fetchedReport.start_date = processDate(fetchedReport.start_date);
          fetchedReport.end_date = processDate(fetchedReport.end_date);

          // Process influencers
          let influencerData = [];
          if (fetchedReport.ReportItem && fetchedReport.ReportItem.length > 0) {
            influencerData = fetchedReport.ReportItem.map((item, index) => ({
              key: Date.now() + index,
              infHandle: item.infHandle || "",
              influencer_handle: item.infHandle || "", // Add for compatibility
              hashtag: item.hashtag || "",
              hash_tag: item.hashtag || "", // Keep both
              token: 0,
            }));
          }

          // Initialize editor state
          let initialEditorState = EditorState.createEmpty();
          if (fetchedReport.description) {
            try {
              const descHasValidJson = utils.testJSON(fetchedReport.description);
              if (descHasValidJson) {
                const contentState = convertFromRaw(JSON.parse(fetchedReport.description));
                initialEditorState = EditorState.createWithContent(contentState);
              } else {
                const contentState = ContentState.createFromText(fetchedReport.description);
                initialEditorState = EditorState.createWithContent(contentState);
              }
            } catch (e) {
              console.error("Error parsing description:", e);
            }
          }

          if (fetchedReport.influencer_report && fetchedReport.influencer_report.length > 0) {
            influencerData = fetchedReport.influencer_report;
          }
          // Set all states
          setForm(fetchedReport);
          setEditorState(initialEditorState);
          setData(influencerData.length > 0 ? influencerData : data);

          if (fetchedReport.status === "archived") {
            setReadOnly(true);
          }

          setIsLoading(false);
          onHeaderTitleChange && onHeaderTitleChange("Edit Report");
        } catch (err) {
          console.error("Error fetching report:", err);
          addToast({
            error: true,
            msg: err.message || "Failed to load report. Please try again.",
            id: `${Date.now()}-${toastList.length}`,
          });
          setIsLoading(false);
          // Optionally navigate back on error
          setTimeout(() => navigate("/brand/reports"), 2000);
        }
      } else if (mode === "create") {
        setIsLoading(false);
        onHeaderTitleChange && onHeaderTitleChange("Create Report");
      }
    };

    fetchReportData();
    // eslint-disable-next-line 
  }, [id, mode]);

  // Update form function
  const updateForm = useCallback((updatedData) => {
    setForm(prev => ({ ...prev, ...updatedData }));
  }, []);

  // Update editor description
  const updateTextDescription = useCallback((state) => {
    setEditorState(state);
    const rawContentState = convertToRaw(state.getCurrentContent());
    updateForm({ description: JSON.stringify(rawContentState) });
  }, [updateForm]);

  // Handle text input changes
  const handleInputChange = useCallback((key) => (event) => {
    updateForm({ [key]: event.target.value });
  }, [updateForm]);

  // Handle platform selection
  const handlePlatformChange = useCallback((item) => {
    updateForm({ platform_id: item.id });
  }, [updateForm]);

  // Handle date changes
  const handleDateChange = useCallback((key) => (date) => {
    updateForm({ [key]: date });
  }, [updateForm]);

  // Validate form
  const validateForm = () => {
    const errors = [];
    let isInfluencerInvalid = false;

    // Check required fields
    if (!form.title || form.title.trim() === "") {
      errors.push("Title is required");
    }

    if (!form.platform_id) {
      errors.push("Platform is required");
    }

    // Improved description validation - check plain text from editor
    const descText = editorState.getCurrentContent().getPlainText().trim();
    if (!descText) {
      errors.push("Description is required");
    }

    if (!form.start_date) {
      errors.push("Start date is required");
    }

    if (!form.end_date) {
      errors.push("End date is required");
    }

    // Check date validity
    if (form.start_date && form.end_date) {
      const start = new Date(form.start_date);
      const end = new Date(form.end_date);

      if (end < start) {
        errors.push("End date must be after start date");
      }

      if (mode === "create") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (start < today) {
          errors.push("Start date cannot be in the past");
        }
      }
    }

    // Check influencers in all modes
    const hasEmptyInfluencer = data.some(item =>
      !item.infHandle || item.infHandle.trim() === "" ||
      !item.hash_tag || item.hash_tag.trim() === ""
    );

    if (hasEmptyInfluencer) {
      setInfError(true);
      isInfluencerInvalid = true;
      // Note: We don't push to errors array because we want to avoid showing a toast
      // for this error (as it's already shown inline in the AddInfluencer component).
    } else {
      setInfError(false);
    }

    // Check for duplicate handles
    if (hasDuplicateHandles) {
      setInfError(true);
      isInfluencerInvalid = true;
    }

    // Check field lengths
    if (form.title && form.title.length > 60) {
      errors.push("Title must be 60 characters or less");
    }

    if (descText.length > 1500) {
      errors.push("Description must be 1500 characters or less");
    }

    // Set error state to trigger UI messages for basic fields
    if (errors.length > 0) {
      setError(true);
    } else {
      setError(false);
    }

    return { errors, isInfluencerInvalid };
  };

  // Save or update report
  const handleSave = async () => {
    const { errors, isInfluencerInvalid } = validateForm();

    // If there are local validation errors, stop here.
    // We don't show toasts for these anymore as they are shown inline below the inputs.
    if (errors.length > 0 || isInfluencerInvalid) {
      return;
    }

    try {
      setIsSaving(true);

      // Prepare data
      const reportData = {
        title: form.title.trim(),
        description: form.description,
        start_date: format(new Date(form.start_date), "yyyy-MM-dd"),
        end_date: format(new Date(form.end_date), "yyyy-MM-dd"),
        platform_id: form.platform_id,
        ReportItem: data.map(item => ({
          infHandle: item.infHandle.trim(),
          hashtag: item.hash_tag.trim(),
          token: item.token || 0
        }))
      };

      let res;
      if (mode === "edit") {
        res = await reportService.updateReport(reportData, id);
      } else {
        res = await reportService.saveReport(reportData);
      }

      if (res.success === true) {
        addToast({
          error: false,
          msg: res.message || "Report saved successfully",
          id: `${Date.now()}-${toastList.length}`,
        });

        if (refreshReports) {
          refreshReports();
        }

        // Navigate back to reports list
        setTimeout(() => {
          navigate("/brand/reports");
        }, 1000);
      } else {
        // Show error message from API response
        addToast({
          error: true,
          msg: res.message || "Failed to save report",
          id: `${Date.now()}-${toastList.length}`,
        });
      }
    } catch (err) {
      console.error("Save error:", err);
      addToast({
        error: true,
        msg: err.message || "An error occurred while saving the report",
        id: `${Date.now()}-${toastList.length}`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle archive/restore
  const handleArchive = () => {
    if (id) {
      archiveReport(id);
    }
  };

  const handleRestore = () => {
    if (id) {
      restoreReport(id);
    }
  };

  const handleStatusUpdate = (status) => {
    if (id) {
      updateStatus(id, status);
    }
  };

  // Utility function
  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading report data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header Section */}
      <header className="flex pb-4 flex-col md:flex-row md:items-center md:justify-between gap-3">
        {/* Left Side - Back Button */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/brand/reports")}
            className="flex h-[38px] px-5 justify-center items-center rounded-full text-sm font-medium cursor-pointer transition-all duration-200 bg-gray-50 text-gray-600 hover:bg-gray-100"
            style={{
              boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
            }}
          >
            <ArrowLeft strokeWidth={2.2} size={18} className="mr-2" />
            Back
          </button>
        </div>

        {/* Right Side - Archive/Restore, Tokens and Status */}
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          {/* Archive/Restore Buttons */}
          {mode === "edit" && (
            <div className="flex items-center gap-2">
              {form.status !== "archived" && (
                <button
                  onClick={handleArchive}
                  className="flex h-[38px] px-4 items-center gap-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200"
                >
                  Archive
                </button>
              )}
              {form.status === "archived" && (
                <button
                  onClick={handleRestore}
                  className="flex h-[38px] px-4 items-center gap-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200"
                >
                  Restore
                </button>
              )}
            </div>
          )}

          {/* Tokens Display */}
          <div
            className="flex h-[38px] px-5 justify-center items-center rounded-full text-sm font-medium cursor-pointer transition-all duration-200 bg-gray-50 text-gray-600"
            style={{
              boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
            }}
          >
            Tokens: Available{" "}
            <span className="text-emerald-600 font-semibold mx-1">
              {tokenAvailable ? tokenAvailable.available : 600}
            </span>
            | Used <span className="text-gray-500 mx-1">0</span>
          </div>

          {/* Status Display and Controls */}
          <div className="flex items-center gap-3">
            {form.status && form.status !== "archived" && mode === "edit" && (
              <div
                className="flex h-[38px] px-5 justify-center items-center rounded-full text-sm font-medium cursor-pointer transition-all duration-200 bg-gray-50 text-gray-600"
                style={{
                  boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                }}
              >
                <button
                  className="p-2 rounded-md hover:bg-gray-100"
                  onClick={() => handleStatusUpdate("active")}
                  title="Set Active"
                >
                  {playIconReport}
                </button>
                <button
                  className="p-2 rounded-md hover:bg-gray-100"
                  onClick={() => handleStatusUpdate("paused")}
                  title="Set Paused"
                >
                  {pauseIconReport}
                </button>
              </div>
            )}

            {form.status && (
              <div className={`px-4 py-2 rounded-full text-sm font-medium border ${form.status === 'active' ? 'bg-green-50 text-green-700 border-green-100' :
                form.status === 'paused' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' :
                  form.status === 'archived' ? 'bg-gray-50 text-gray-700 border-gray-100' :
                    'bg-blue-50 text-blue-700 border-blue-100'
                }`}>
                Status: {capitalizeFirstLetter(form.status)}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Form Content */}
      <div
        className="rounded-xl bg-white overflow-hidden w-full flex-1 min-h-0"
        style={{
          boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
          border: "1px solid rgba(16, 185, 129, 0.1)",
        }}
      >
        <div
          className="p-6 flex-1 overflow-y-auto"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#e5e7eb transparent",
          }}
        >
          {/* Title and Platform Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <CustomTextInput
              label="Title"
              value={form.title}
              onChange={handleInputChange("title")}
              error={error && !form.title}
              errorText="Please enter Title"
              readOnly={readonly}
            />

            <CustomSelect
              label="Platform"
              selected={platform.find(
                (item) => String(item.id) === String(form.platform_id)
              )}
              onChange={handlePlatformChange}
              list={platform}
              error={error && !form.platform_id}
              errorText="Please select Platform"
              readOnly={readonly}
            />
          </div>

          {/* Description Field */}
          <div className="mb-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              Description
              <span className="text-red-500">*</span>
            </label>
            <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-1 focus-within:ring-green-500 focus-within:border-green-500">
              <EditorInput
                editorState={editorState}
                onEditorStateChange={updateTextDescription}
                readOnly={readonly}
              />
            </div>
            {error && !editorState.getCurrentContent().hasText() && (
              <div className="text-sm text-red-600 mt-1">
                <div className="flex items-center gap-2">
                  Please enter description
                </div>
              </div>
            )}
          </div>

          {/* Date Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <CustomDateInput
              label="Start Date"
              value={form.start_date}
              onChange={handleDateChange("start_date")}
              error={error && !form.start_date}
              errorText="Please enter Start date"
              readOnly={readonly}
              minDate={mode === "create" ? new Date() : new Date()}
            />

            <CustomDateInput
              label="End Date"
              value={form.end_date}
              onChange={handleDateChange("end_date")}
              error={error && !form.end_date}
              errorText="Please enter End date"
              readOnly={readonly}
              minDate={form.start_date}
            />
          </div>

          {/* Add Influencer Section */}
          <div className="mb-4">
            <AddInfluencer
              status={form.status}
              infError={infError}
              error={error}
              data={data}
              setData={setData}
              startDate={form.start_date}
              endDate={form.end_date}
              onDuplicateHandlesChange={setHasDuplicateHandles}
              mode={mode}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end items-center gap-4 mt-6">
            <button
              className="flex h-[42px] px-6 items-center gap-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 bg-gray-50 text-gray-600 hover:bg-gray-100 border-none"
              style={{
                boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
              }}
              onClick={() => navigate("/brand/reports")}
              disabled={isSaving}
            >
              Cancel
            </button>

            {form.status !== "archived" ? (
              <button
                className="flex h-[42px] px-6 items-center gap-2 rounded-full text-sm font-semibold cursor-pointer border-none text-white transition-all duration-200 hover:opacity-90 disabled:opacity-50"
                style={{
                  background:
                    "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                  boxShadow: "0px 2px 6px 0px rgba(16, 185, 129, 0.25)",
                }}
                onClick={handleSave}
                disabled={isSaving || readonly}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {mode === "create" ? "Creating..." : "Saving..."}
                  </>
                ) : (
                  mode === "create" ? "Create Report" : "Save Changes"
                )}
              </button>
            ) : (
              <button
                className="flex h-[42px] px-6 items-center gap-2 rounded-full text-sm font-semibold cursor-pointer border-none text-white transition-all duration-200 hover:opacity-90"
                style={{
                  background:
                    "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
                  boxShadow: "0px 2px 6px 0px rgba(59, 130, 246, 0.25)",
                }}
                onClick={handleRestore}
              >
                Restore Report
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}